import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { Zap, AlertTriangle, Activity, Download, RotateCcw, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';

import { API_URL } from '../config';

export default function EESPage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('ees-data');
      return saved ? JSON.parse(saved) : {
        indications: [],
        materiel: {},
        parametres: { fc: '60', intensite: '50' },
        etapes: {}
      };
    } catch {
      return { indications: [], materiel: {}, parametres: { fc: '60', intensite: '50' }, etapes: {} };
    }
  });

  useEffect(() => {
    localStorage.setItem('ees-data', JSON.stringify(formData));
  }, [formData]);

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse en cours...');
      const response = await fetch(`${API_URL}/api/ai/analyze-ees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Erreur');
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminee');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const indications = [
    'Dysfonction sino-auriculaire (pauses, BSA)',
    'Bradycardie jonctionnelle',
    'Bloc auriculo-ventriculaire haut degre'
  ];

  const materiel = [
    'Electrodes QUIK-COMBO',
    'Raccord',
    'Lifepak 12 ou 15'
  ];

  const etapes = [
    'Sedation/analgesie si patient conscient',
    'Placement electrodes',
    'Reglage FC initiale (60-80 bpm)',
    'Intensite 50mA puis monter de 10 en 10',
    'Verifier capture (QRS apres chaque spike)',
    'Surveillance continue'
  ];

  return (
    <div className="p-3 md:p-6 space-y-3 max-w-4xl mx-auto">
      <div className="space-y-2 print:hidden">
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-white">EES</h1>
              <p className="text-xs text-gray-400">Entrainement Electro-Systolique Externe</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyze} disabled={isAnalyzing} size="sm" className="bg-purple-500/20">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={() => { localStorage.removeItem('ees-data'); window.location.reload(); }} size="sm" variant="outline">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Alert className="bg-yellow-500/10 border-yellow-500/30 p-2">
          <Zap className="h-4 w-4 text-yellow-400" />
          <AlertDescription className="text-xs text-yellow-300">Stimulation cardiaque externe temporaire</AlertDescription>
        </Alert>
      </div>

      <div ref={printRef} className="space-y-3">
        <Card className="border-cyan-500/30 bg-cyan-500/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm text-cyan-300">INDICATIONS</CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {indications.map((ind, i) => (
              <label key={i} className="flex items-center gap-2 p-2 rounded bg-gray-800 cursor-pointer">
                <Checkbox
                  checked={formData.indications.includes(ind)}
                  onCheckedChange={() => {
                    const newInd = formData.indications.includes(ind)
                      ? formData.indications.filter(x => x !== ind)
                      : [...formData.indications, ind];
                    setFormData(prev => ({ ...prev, indications: newInd }));
                  }}
                />
                <span className="text-xs text-white">{ind}</span>
              </label>
            ))}
          </CardContent>
        </Card>

        <Card className="border-blue-500/30">
          <CardHeader className="p-3">
            <CardTitle className="text-sm text-blue-300">MATERIEL</CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {materiel.map((mat, i) => (
              <label key={i} className="flex items-center gap-2 p-2 rounded bg-gray-800 cursor-pointer">
                <Checkbox
                  checked={formData.materiel[i]}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, materiel: { ...prev.materiel, [i]: c } }))}
                />
                <span className="text-xs text-white">{mat}</span>
              </label>
            ))}
          </CardContent>
        </Card>

        <Card className="border-purple-500/30">
          <CardHeader className="p-3">
            <CardTitle className="text-sm text-purple-300">PARAMETRES</CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Frequence cible (bpm)</label>
              <Input
                type="number"
                value={formData.parametres.fc}
                onChange={(e) => setFormData(prev => ({ ...prev, parametres: { ...prev.parametres, fc: e.target.value } }))}
                className="h-9"
              />
              <p className="text-xs text-cyan-300 mt-1">Initial: 60-80 bpm</p>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Intensite (mA)</label>
              <Input
                type="number"
                value={formData.parametres.intensite}
                onChange={(e) => setFormData(prev => ({ ...prev, parametres: { ...prev.parametres, intensite: e.target.value } }))}
                className="h-9"
              />
              <p className="text-xs text-cyan-300 mt-1">Debut 50mA, augmenter de 10 en 10 jusqua capture</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-500/30">
          <CardHeader className="p-3">
            <CardTitle className="text-sm text-green-300">METHODE (6 etapes)</CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {etapes.map((etape, i) => (
              <div
                key={i}
                onClick={() => setFormData(prev => ({ ...prev, etapes: { ...prev.etapes, [i]: !prev.etapes[i] } }))}
                className="flex items-start gap-2 p-2 rounded bg-gray-800 cursor-pointer"
              >
                <Checkbox checked={formData.etapes[i] || false} />
                <div className="flex-1">
                  <Badge variant="outline" className="text-xs mb-1">{i + 1}</Badge>
                  <div className="text-xs text-white">{etape}</div>
                </div>
                {formData.etapes[i] && <CheckCircle2 className="w-4 h-4 text-green-400" />}
              </div>
            ))}
          </CardContent>
        </Card>

        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />ANALYSE IA
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="whitespace-pre-wrap text-xs text-white">{aiAnalysis.analysis}</div>
              <Alert className="mt-3 bg-amber-500/10 border-amber-500/30 p-2">
                <AlertDescription className="text-xs text-amber-300">Disclaimer: Aide decision, validation medicale requise</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
